import React, { useEffect, useRef, useState } from 'react';
import { ArrowForward, CheckCircleRounded, FitnessCenter, PersonAddAlt1, PeopleAlt, PlayArrow, Pause, QrCode2, Autorenew } from '@mui/icons-material';
import './GymJourney.css';

const STEPS = [
  { title: 'Capture', subtitle: 'An enquiry becomes a lead', icon: PeopleAlt, color: '#7c3aed',
    heading: 'Give every enquiry a next step.',
    story: 'Aarav asks about your gym. Your team saves the enquiry, tracks the conversation, and follows up from one place.',
    benefit: 'Your team knows who to contact next.',
    owner: 'Track leads and follow-ups', member: 'Get a clear path to joining',
    screen: 'Lead pipeline', status: 'Follow-up scheduled', rows: ['New enquiry received', 'Gym visit arranged', 'Follow-up assigned'], action: 'Move to membership', tag: 'NEW ENQUIRY' },
  { title: 'Welcome', subtitle: 'A lead becomes a member', icon: PersonAddAlt1, color: '#2563eb',
    heading: 'Start the membership with everything in place.',
    story: 'After the visit, Aarav joins. Set up his membership, record the payment, and keep his profile and invoice together.',
    benefit: 'Less time searching through records.',
    owner: 'Manage membership and billing', member: 'See membership details in the app',
    screen: 'Member onboarding', status: 'Membership active', rows: ['Member profile created', 'Membership assigned', 'Payment recorded'], action: 'Ready for the first visit', tag: 'MEMBER JOINED' },
  { title: 'Check in', subtitle: 'Every visit is recorded', icon: QrCode2, color: '#0891b2',
    heading: 'Make the first visit feel effortless.',
    story: 'Aarav arrives for a session. QR or supported biometric check-in records his attendance so your team can see his visits.',
    benefit: 'A clear attendance record for your team.',
    owner: 'See attendance in one place', member: 'Check in and get training',
    screen: 'Attendance', status: 'Checked in today', rows: ['Member identified', 'Attendance recorded', 'Visit history updated'], action: 'Time to train', tag: 'AT THE GYM' },
  { title: 'Train', subtitle: 'A plan becomes a routine', icon: FitnessCenter, color: '#059669',
    heading: 'Keep coaching connected to daily effort.',
    story: 'His trainer assigns workouts and a diet plan. Aarav follows his sessions, logs meals, and tracks his progress in the member app.',
    benefit: 'Coaches and members work from the same plan.',
    owner: 'Assign workouts and nutrition', member: 'Follow plans and log progress',
    screen: 'Member activity', status: 'Today’s session logged', rows: ['Workout plan assigned', 'Meals organised by day', 'Progress recorded'], action: 'Build a consistent routine', tag: 'MAKING PROGRESS' },
  { title: 'Reconnect', subtitle: 'Stay close to the next milestone', icon: Autorenew, color: '#d97706',
    heading: 'Know when it is time to reach out.',
    story: 'As Aarav’s membership approaches expiry, your team reviews his activity and follows up about his next membership.',
    benefit: 'Useful context for every renewal conversation.',
    owner: 'Review activity and upcoming expiry', member: 'Continue the fitness journey',
    screen: 'Member follow-up', status: 'Renewal follow-up due', rows: ['Membership expiry visible', 'Activity history available', 'Next conversation planned'], action: 'Keep the journey going', tag: 'WHAT’S NEXT' },
];

export default function GymJourney({ onBookDemo }) {
  const section = useRef(null);
  const [active, setActive] = useState(0);
  const [playing, setPlaying] = useState(true);
  const [visible, setVisible] = useState(false);
  const [reducedMotion, setReducedMotion] = useState(false);
  useEffect(() => {
    const query = window.matchMedia('(prefers-reduced-motion: reduce)');
    const update = () => setReducedMotion(query.matches);
    update();
    query.addEventListener('change', update);
    const observer = new IntersectionObserver(([entry]) => setVisible(entry.isIntersecting), { threshold: 0.25 });
    observer.observe(section.current);
    return () => { query.removeEventListener('change', update); observer.disconnect(); };
  }, []);
  useEffect(() => {
    if (!playing || !visible || reducedMotion) return undefined;
    const timer = setInterval(() => setActive(value => (value + 1) % STEPS.length), 6500);
    return () => clearInterval(timer);
  }, [playing, visible, reducedMotion]);
  const step = STEPS[active];
  const select = index => { setActive(index); setPlaying(false); };
  return (
    <section id="how-it-works" className="gym-journey" ref={section} aria-labelledby="journey-title"
      style={{ '--journey-accent': step.color }}>
      <div className="journey-inner">
        <div className="journey-intro">
          <span className="journey-eyebrow">ONE MEMBER. ONE CONNECTED JOURNEY.</span>
          <h2 id="journey-title">From first hello to<br /><span>their next milestone.</span></h2>
          <p>See how Fitistic brings your front desk, coaches, and members together through a typical gym journey.</p>
        </div>
        <div className="journey-controls">
          <span>Follow Aarav’s journey <span className="journey-example">· Illustrative walkthrough</span></span>
          {!reducedMotion && <button type="button" onClick={() => setPlaying(value => !value)} aria-label={playing ? 'Pause journey' : 'Play journey'}>
            {playing ? <Pause fontSize="small" /> : <PlayArrow fontSize="small" />}{playing ? 'Pause' : 'Play'}
          </button>}
        </div>
        <ol className="journey-flow" aria-label="Member journey stages">
          {STEPS.map((item, index) => {
            const Icon = item.icon;
            return <li key={item.title}>
              <button type="button" className={index === active ? 'journey-step is-active' : 'journey-step'}
                aria-pressed={index === active} aria-controls="journey-detail" onClick={() => select(index)}>
                <span className="journey-step-icon"><Icon /><small>{index + 1}</small></span>
                <strong>{item.title}</strong><span>{item.subtitle}</span>
              </button>
              {index < STEPS.length - 1 && <ArrowForward className="journey-connector" aria-hidden="true" />}
            </li>;
          })}
        </ol>
        <div id="journey-detail" className="journey-detail" aria-live={playing ? 'off' : 'polite'}>
          <div className="journey-story" key={`story-${active}`}>
            <span className="journey-eyebrow">0{active + 1} / {step.title.toUpperCase()}</span>
            <h3>{step.heading}</h3>
            <p>{step.story}</p>
            <div className="journey-benefit"><CheckCircleRounded fontSize="small" /><strong>{step.benefit}</strong></div>
            <div className="journey-roles">
              <div><span>FOR YOUR TEAM</span><strong>{step.owner}</strong></div>
              <div><span>FOR YOUR MEMBER</span><strong>{step.member}</strong></div>
            </div>
          </div>
          <div className="journey-preview" key={`preview-${active}`} aria-label={`${step.screen} illustration`}>
            <div className="journey-preview-top"><span><i /> Fitistic</span><span>{step.screen}</span></div>
            <div className="journey-member"><div className="journey-avatar">A</div><div><strong>Aarav’s journey</strong><span>{step.tag}</span></div><span className="journey-stage-count">0{active + 1}</span></div>
            <div className="journey-status"><span />{step.status}</div>
            <div className="journey-events">{step.rows.map((row, index) => <div key={row} style={{ '--event-delay': `${index * 130}ms` }}><CheckCircleRounded fontSize="small" /><span>{row}</span></div>)}</div>
            <div className="journey-next"><span>{step.action}</span><ArrowForward fontSize="small" /></div>
          </div>
        </div>
        <div className="journey-footer"><p>One shared view. A smoother day for everyone.</p><button type="button" onClick={onBookDemo}>See it for your gym <ArrowForward fontSize="small" /></button></div>
      </div>
    </section>
  );
}
